import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { ConfirmationDialog, PageHeader, useToast } from 'components/admin';
import { Button, Input, Select, Table } from 'components/common';
import { deleteProjectInProgress, getAdminProjectsInProgress } from 'apis/adminProjectsInProgress';
import { getAdminEcosystemGroups } from 'apis/adminEcosystemGroups';
import { useActiveLanguagesQuery, useAdminDeleteMutation, useAdminListQuery } from 'hooks';
import '../../../styles/admin-shared.scss';

const ProjectsInProgressList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [keyword, setKeyword] = useState('');
    const [isActiveFilter, setIsActiveFilter] = useState('');
    const [languageCodeFilter, setLanguageCodeFilter] = useState('vi');
    const [ecosystemGroupFilter, setEcosystemGroupFilter] = useState('');
    const [deleteId, setDeleteId] = useState(null);
    const toast = useToast();
    const navigate = useNavigate();

    const queryParams = useMemo(() => ({
        pageIndex: currentPage,
        pageSize,
        keyword: keyword || undefined,
        isActive: isActiveFilter === '' ? undefined : isActiveFilter === 'true',
        languageCode: languageCodeFilter || undefined,
        ecosystemGroupId: ecosystemGroupFilter || undefined,
    }), [currentPage, pageSize, keyword, isActiveFilter, languageCodeFilter, ecosystemGroupFilter]);

    const { data = { items: [], total: 0, currentPage, pageSize }, isFetching, error } = useAdminListQuery('projects-in-progress', queryParams, getAdminProjectsInProgress);
    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: ecosystemData = { items: [] } } = useAdminListQuery(
        'projects-in-progress-ecosystem-filter',
        { pageSize: 100, languageCode: languageCodeFilter || 'vi', isActive: true },
        getAdminEcosystemGroups
    );
    const deleteMutation = useAdminDeleteMutation('projects-in-progress', deleteProjectInProgress);

    useEffect(() => {
        setCurrentPage(1);
    }, [keyword, isActiveFilter, languageCodeFilter, ecosystemGroupFilter]);

    useEffect(() => {
        if (error) toast.error(error.message || 'Tải dữ liệu thất bại');
    }, [error, toast]);

    const handleDelete = async () => {
        try {
            const res = await deleteMutation.mutateAsync(deleteId);
            if (res.success) { toast.success('Xóa thành công'); } else toast.error(res.message);
        } catch { toast.error('Xóa thất bại'); }
        setDeleteId(null);
    };

    const columns = [
        { field: 'id', header: 'ID' },
        {
            header: 'Hình ảnh',
            render: (row) => row.imageUrl ? <img src={row.imageUrl} alt="img" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} /> : <span style={{ color: '#ccc', fontSize: 12 }}>Chưa có</span>,
        },
        { field: 'name', header: 'Tên dự án' },
        { 
            header: 'Mô tả', 
            render: (row) => row.description ? (
                <div 
                    dangerouslySetInnerHTML={{ __html: row.description }}
                    style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '300px'
                    }}
                />
            ) : '—'
        },
        { header: 'Hệ sinh thái', render: (row) => row.ecosystemGroup?.name || '—' },
        { field: 'sortOrder', header: 'Thứ tự' },
        { header: 'Trạng thái', render: (row) => <span className={`status-badge ${row.isActive ? 'active' : 'inactive'}`}>{row.isActive ? 'Hoạt động' : 'Không hoạt động'}</span> },
        { header: 'Thao tác', render: (row) => <div className="action-buttons"><button className="icon-btn icon-btn--edit" onClick={() => navigate(`/admin/projects-in-progress/edit/${row.id}`)} title="Sửa"><FiEdit2 size={15} /></button><button className="icon-btn icon-btn--delete" onClick={() => setDeleteId(row.id)} title="Xóa"><FiTrash2 size={15} /></button></div> },
    ];

    return (
        <div className="admin-page">
            <PageHeader title="Dự án đang triển khai" subtitle="Quản lý danh sách các dự án đang triển khai" action={<Button onClick={() => navigate('/admin/projects-in-progress/create')}>+ Thêm Dự án</Button>} />
            <div className="filters-bar">
                <div className="filter-item"><Input placeholder="Tìm tên dự án..." value={keyword} onChange={e => setKeyword(e.target.value)} /></div>
                <div className="filter-item"><Select value={isActiveFilter} onChange={e => setIsActiveFilter(e.target.value)} options={[{ value: '', label: 'Tất cả trạng thái' }, { value: 'true', label: 'Hoạt động' }, { value: 'false', label: 'Không hoạt động' }]} /></div>
                <div className="filter-item">
                    <Select value={languageCodeFilter} onChange={(e) => setLanguageCodeFilter(e.target.value)}
                        options={[...languages.map(l => ({ value: l.code, label: l.name }))]} />
                </div>
                <div className="filter-item">
                    <Select
                        value={ecosystemGroupFilter}
                        onChange={(e) => setEcosystemGroupFilter(e.target.value)}
                        options={[
                            { value: '', label: 'Tất cả hệ sinh thái' },
                            ...ecosystemData.items.map(group => ({
                                value: String(group.id),
                                label: group.name || `#${group.id}`,
                            })),
                        ]}
                    />
                </div>
            </div>
            <Table columns={columns} data={data.items} loading={isFetching} pagination={{ currentPage: data.currentPage, totalPages: Math.ceil(data.total / data.pageSize) || 1, onPageChange: setCurrentPage }} />
            <ConfirmationDialog open={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} title="Xóa Dự án" message="Bạn có chắc chắn muốn xóa không?" />
        </div>
    );
};

export default ProjectsInProgressList;
